import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { KeysTable, type ApiKeyRow } from '../components/admin/KeysTable'
import { CopyField } from '../components/CopyField'
import { useAuth } from '../auth/AuthProvider'

/** The signed-in user's page: their API keys, and for an admin everyone's (with the owner column), created and revoked here. */
export function AccountScreen() {
	const { user } = useAuth()
	const client = useQueryClient()
	const admin = user?.role === 'admin'
	const keysUrl = admin ? '/api/admin/keys' : '/api/keys'
	const keys = useQuery({ queryKey: ['keys', keysUrl], queryFn: () => api.get<ApiKeyRow[]>(keysUrl) })
	const [name, setName] = useState('')
	const [created, setCreated] = useState<string | null>(null)
	const refresh = () => client.invalidateQueries({ queryKey: ['keys'] })
	const create = useMutation({
		mutationFn: () => api.post<{ key: string }>('/api/keys', { name }),
		onSuccess: (r) => {
			setCreated(r.key)
			setName('')
			refresh()
		},
	})
	const revoke = useMutation({ mutationFn: (id: string) => api.delete(`/api/keys/${id}`), onSuccess: refresh })
	function submit(e: FormEvent) {
		e.preventDefault()
		create.mutate()
	}
	return (
		<div className="stack">
			<div className="page-header">
				<div>
					<h1>{user?.name}</h1>
					<p className="muted small" style={{ margin: 0 }}>
						{user?.email} · {user?.role}
					</p>
				</div>
			</div>
			<section className="card stack">
				<div>
					<h2 style={{ margin: 0 }}>API keys</h2>
					<p className="muted small" style={{ margin: 0 }}>
						Use a key as <code>Authorization: Bearer …</code> with the API. <a href="/api/docs">API documentation</a>
						{admin && <> · Every user’s keys are listed; a new key is yours.</>}
					</p>
				</div>
				<form className="row" onSubmit={submit}>
					<input
						required
						placeholder="Key name, e.g. laptop script"
						aria-label="Key name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						style={{ flex: '1 1 220px' }}
					/>
					<button className="primary" type="submit" disabled={create.isPending}>
						Create key
					</button>
				</form>
				{created && (
					<div>
						<p className="small" style={{ margin: '0 0 0.3rem' }}>
							Copy this key now — it won’t be shown again:
						</p>
						<CopyField label="New API key" value={created} />
					</div>
				)}
				<KeysTable keys={keys.data ?? []} onRevoke={(id) => revoke.mutate(id)} />
			</section>
		</div>
	)
}
